/**
 * Settings.jsx
 *
 * Account management page allowing users to update personal information
 * and perform destructive account actions.
 *
 * Features:
 * - Update username, email, and password
 * - Reset prediction history
 * - Delete user account
 * - Fetch and display current user profile data
 *
 * Responsibilities:
 * - Handles API communication for account updates
 * - Manages form state for user inputs
 * - Provides confirmation prompts for destructive actions
 *
 * Data Endpoints:
 * - PUT    /api/users/updateUsername
 * - PUT    /api/users/updateEmail
 * - PUT    /api/users/updatePassword
 * - DELETE /api/users/resetPredictions
 * - DELETE /api/users/deleteAccount
 * - GET    /api/users/profile
 *
 * Notes:
 * - Uses session-based authentication (credentials: "include")
 * - Relies on backend to validate and enforce security rules
 * - Destructive actions require user confirmation before execution
 */

import React, { useState, useEffect } from 'react';
import {useNavigate} from 'react-router-dom';
import '../css/Settings.css';

const Settings = () => {

    // Navigation hook for redirecting after account actions
    const navigate = useNavigate();

    // Form state for updating account details
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');

    // Current user data fetched from backend
    const [currentUsername, setCurrentUsername] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');

    /**
     * handleUpdateEmail()
     *
     * Sends request to update user's email address.
     * Displays server response message upon completion.
     */
    const handleUpdateEmail = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/updateEmail`, {
            //const response = await fetch('http://localhost:5001/api/users/updateEmail', {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({ email }),
            });
            const data = await response.json();
            alert(data.message);
        } 
        catch (error) {
            console.error('Error updating email:', error);
        }
    };

    /**
     * handleUpdatePassword()
     *
     * Updates user password using current password verification.
     * Backend is responsible for validating correctness and hashing.
     */
    const handleUpdatePassword = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/updatePassword`, {
            //const response = await fetch('http://localhost:5001/api/users/updatePassword', {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({ currentPassword, password })
            });
            const data = await response.json();
            alert(data.message);
        }
        catch (error) {
            console.error('Error updating password:', error);
        }
    };

    /**
     * handleUpdateUsername()
     *
     * Updates the user's username.
     */
    const handleUpdateUsername = async ()=> {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/updateUsername`, {
            //const response = await fetch('http://localhost:5001/api/users/updateUsername', {
                method: "PUT",
                headers: {"Content-Type":"application/json"},
                credentials: "include",
                body: JSON.stringify({username})
            });

            const data = await response.json();
            alert(data.message);
        }
        catch(error){
            console.error('Error updating username: ', error);
        }
    };

    /**
     * handleResetPredictions()
     *
     * Deletes all user prediction history after confirmation.
     * This action is irreversible.
     */
    const handleResetPredictions = async () => {
        const confirmed = window.confirm("Are you sure you want to reset all your prediction history. This cannot be undone.");
        if(!confirmed) return;

        try{
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/resetPredictions`, {
            //const response = await fetch('http://localhost:5001/api/users/resetPredictions', {
                method: "DELETE",
                credentials: "include",
            });
            
            const data= await response.json;
        }
        catch(error) {
            console.error('Error resseting predictions',error);
        }
    };

    /**
     * handleDeleteAccount()
     *
     * Permanently deletes the user account after confirmation.
     * Redirects user to landing page upon success.
     */
    const handleDeleteAccount = async()=> {
        const confirmed = window.confirm('Are you sure you want to delete your account? This cannot be undone.');
        if(!confirmed) return;

        try{
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/deleteAccount`, {
            //const response = await fetch('http://localhost:5001/api/users/deleteAccount', {
                method: "DELETE",
                credentials: "include",
            });

            const data = await response.json;
            alert(data.message);
            navigate('/');
        }
        catch(error){
            console.error("Error deleting account:", error);
        }
    };

    /**
     * Fetch current user profile data on component mount
     * Populates UI with existing username and email
     */
    useEffect(() => {
        const fetchUserInfo = async () => {
            try{
                const response  = await fetch(`${process.env.REACT_APP_API_URL}/api/users/profile`, {
                    method: "GET",
                    credentials: "include",
                });
                const data = await response.json();
                setCurrentUsername(data.username);
                setCurrentEmail(data.email);
            }
            catch(error){
                console.error('Error fetching user info:', error);
            }
        };
        fetchUserInfo();
    }, []);

    return (
        <div className="settings-page">
            <div className="account-settings">
                <h2>Account Settings</h2>

                {/* Username Update */}
                <div className="settings-section">
                    <h3>Change Username</h3>
                    <p>Current Username: <strong>{currentUsername}</strong></p>
                    <input
                        type="text"
                        placeholder="New Username"
                        value={username}
                        onChange={(e)=> setUsername(e.target.value)}
                    />
                    <button className="update-btn" onClick={handleUpdateUsername}>Update</button>
                </div>

                {/* Email Update */}
                <div className="settings-section">
                    <h3>Change Email</h3>
                    <p>Current Email: <strong>{currentEmail}</strong></p>
                    <input
                        type="email"
                        placeholder="New Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <button className="update-btn" onClick={handleUpdateEmail}>Update</button>
                </div>

                {/* Password Update */}
                <div className="settings-section">
                    <h3>Change Password</h3>
                    <input
                        type="password"
                        placeholder="Current Password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                    <input
                        type="password"
                        placeholder="New Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <button className="update-btn" onClick={handleUpdatePassword}>Update</button>
                </div>

                {/* Reset Predictions */}
                <div className="settings-section danger-zone">
                    <h3>Reset Prediction History</h3>
                    <p>This will permanetly delete all you past predictions.</p>
                    <button className="danger-btn" onClick={handleResetPredictions}>Reset</button>
                </div>

                {/* Delete Account */}
                <div className="settings-section danger-zone">
                    <h3>Delete Account</h3>
                    <p>This will permanently delete your account and all associated data.</p>
                    <button className="danger-btn" onClick={handleDeleteAccount}>Delete Account</button>
                </div>
            </div>
        </div>
    );
};

export default Settings;